import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProjectDetails.css';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

  useEffect(() => {
    const fetchProject = async () => {
      try {
        console.log('Fetching project with ID:', id);
        const response = await fetch(`${API_BASE_URL}/get_projects.php`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        console.log('API Response:', result);
        
        if (result.success && result.projects) {
          const foundProject = result.projects.find(p => p.id === id || p.id === parseInt(id));
          console.log('Found project:', foundProject);
          
          if (foundProject) {
            const mappedProject = {
              id: foundProject.id,
              projectType: foundProject.project_type,
              clientName: foundProject.client_name,
              projectTitle: foundProject.project_title,
              location: foundProject.location,
              area: foundProject.area,
              floors: foundProject.floors,
              phase: foundProject.phase,
              startDate: foundProject.start_date,
              completionDate: foundProject.completion_date,
              progress: foundProject.progress,
              keyFeatures: foundProject.key_features || '',
              images: foundProject.images || [],
              createdAt: foundProject.created_at
            };
            console.log('Mapped project:', mappedProject);
            setProject(mappedProject);
            // reset selected image to first when project loads
            setSelectedIndex(0);
          } else {
            console.log('Project not found with ID:', id);
          }
        } else {
          console.error('API Error:', result.error);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id, API_BASE_URL]);

  if (!project) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading project details... {id}</p>
        <button className="back-btn" onClick={() => navigate('/dashboard')} style={{marginTop: '20px'}}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const features = project.keyFeatures 
    ? project.keyFeatures.split(',').map(f => f.trim()).filter(f => f) 
    : [];

  return (
    <div className="project-details-container">
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Dashboard
        </button>
      </div>

      <div className="details-content">
        <div className="details-card">
          {/* Project Header */}
          <div className="details-project-header">
            <div className="header-top">
              <div>
                <h1 className="project-detail-title">{project.projectTitle}</h1>
                <p className="project-detail-client">
                  <span className="detail-icon">👤</span>
                  {project.clientName}
                </p>
              </div>
              <span className={`detail-status ${
                project.projectType === 'Completed Projects' ? 'status-completed' :
                project.projectType === 'Ongoing Projects' ? 'status-ongoing' : 'status-upcoming'
              }`}>
                {project.projectType.replace(' Projects', '')}
              </span>
            </div>
          </div>

          {/* Image Gallery */}
          {Array.isArray(project.images) && project.images.length > 0 ? (
            <div className="image-gallery">
              <div className="gallery-main">
                <img
                  src={`${BASE_URL}${(project.images && project.images[selectedIndex]) || project.images[0]}`}
                  alt={project.projectTitle}
                  onError={(e) => {
                    console.log('Image load error:', e);
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3C/svg%3E';
                  }}
                />
              </div>
              {project.images.length > 1 && (
                <div className="gallery-thumbnails">
                  {project.images.map((img, idx) => {
                    // show all images as thumbnails; idx 0 is also clickable
                    const realIndex = idx;
                    return (
                      <div
                        key={realIndex}
                        className={`thumbnail ${selectedIndex === realIndex ? 'active' : ''}`}
                        onClick={() => setSelectedIndex(realIndex)}
                        style={{cursor: 'pointer'}}
                        title={`Show image ${realIndex + 1}`}
                      >
                        <img
                          src={`${BASE_URL}${img}`}
                          alt={`${project.projectTitle} ${realIndex + 1}`}
                          onError={(e) => {
                            console.log('Thumbnail load error:', e);
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="no-image-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="64" height="64">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <p>No images available</p>
            </div>
          )}

          {/* Project Info Grid */}
          <div className="info-grid">
            <div className="info-card">
              <div className="info-card-icon">📍</div>
              <div className="info-card-content">
                <p className="info-card-label">Location</p>
                <p className="info-card-value">{project.location}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-icon">📏</div>
              <div className="info-card-content">
                <p className="info-card-label">Area</p>
                <p className="info-card-value">{project.area} sqft</p>
              </div>
            </div>

            {project.floors && (
              <div className="info-card">
                <div className="info-card-icon">🏢</div>
                <div className="info-card-content">
                  <p className="info-card-label">Floors/Units</p>
                  <p className="info-card-value">{project.floors}</p>
                </div>
              </div>
            )}

            {project.phase && (
              <div className="info-card">
                <div className="info-card-icon">⚙️</div>
                <div className="info-card-content">
                  <p className="info-card-label">Phase/Site</p>
                  <p className="info-card-value">{project.phase}</p>
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="timeline-section">
            <h2 className="section-title">Project Timeline</h2>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-marker start"></div>
                <div className="timeline-content">
                  <p className="timeline-label">Start Date</p>
                  <p className="timeline-date">{formatDate(project.startDate)}</p>
                </div>
              </div>
              <div className="timeline-line"></div>
              <div className="timeline-item">
                <div className={`timeline-marker ${project.projectType === 'Completed Projects' ? 'completed' : 'end'}`}></div>
                <div className="timeline-content">
                  <p className="timeline-label">
                    {project.projectType === 'Completed Projects' ? 'Completion Date' : 'Expected Completion'}
                  </p>
                  <p className="timeline-date">{formatDate(project.completionDate)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Section */}
          <div className="progress-section">
            <h2 className="section-title">Project Progress</h2>
            <div className="progress-detail">
              <div className="progress-bar-container">
                <div 
                  className={`progress-bar-detail ${project.projectType === 'Completed Projects' ? 'completed' : ''}`}
                  style={{ width: `${project.progress}%` }}
                >
                  <span className="progress-text-detail">{project.progress}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features */}
          {features.length > 0 && (
            <div className="features-section">
              <h2 className="section-title">Key Features</h2>
              <div className="features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="feature-tag">
                    <span className="feature-icon">⭐</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project Metadata */}
          <div className="metadata-section">
            <p className="metadata-text">
              <span className="metadata-label">Project Added:</span>
              {new Date(project.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
